// "Hour × Day Call Density" heatmap renderer (RET-4303).
//
// A 7×24 (day-of-week × hour-of-day) heatmap rendered with Chart.js's
// `chartjs-chart-matrix` plugin — the canonical Chart.js heatmap solution, and the same
// "Chart.js + a plugin" pattern the geography (chartjs-chart-geo) and combo cards use.
// Registered in the charts_js_controller renderer registry; the Chart.js canvas legend is
// disabled and the scale lives in the card footer.
//
// Unlike the discrete-series charts, the footer isn't a chip legend — it's a continuous
// color scale (like the choropleth). Both go through the SAME shared htmlLegend plugin: this
// renderer registers it (passed in via `plugins`) and hands it a color-scale model builder at
// `options.plugins.htmlLegend.colorScale`. The controller re-reads that builder on every
// chart.update() and paints the gradient rail into the card footer — so the measure toggle
// repaints the scale for free, and there's no bespoke footer DOM here anymore.
//
// Colors come from the themed palette (charts/palette.js → custom/charts.scss CSS variables)
// so the cyan/violet ramps flip with the active color mode, and the global Chart.js defaults
// (theme.js: tooltip chrome, text color) carry the baseline — we override only the
// heatmap-specific bits.
//
// Reads CallChartable#hour_by_day_density_data: the web-only additive `data.grid`
// (a 7×24 array of { vol, rcv }). The sibling `data.labels`/`data.datasets` are the plain
// hour-of-day bar the /mobile/v1/calls_charts API consumes — ignored here.
//
// One non-persisted knob comes from the card header via charts_js_controller#setDensityMeasure:
//   • measure: 'vol' → cells are call VOLUME, ramped in brand cyan.
//              'rcv' → cells are RECEIVABLES %, ramped in violet.
import { Chart } from 'chart.js'

import { chartPalette } from './palette'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const HOURS = Array.from({ length: 24 }, (_, h) => h)
const hourLabel = (h) => (h === 0 ? '12a' : h === 12 ? '12p' : h > 12 ? `${h - 12}p` : `${h}a`)

// Parse a palette color (#rgb / #rrggbb / rgb()/rgba()) into [r, g, b] so we can grade a
// single themed hue by alpha for the cell ramp + footer gradient, instead of carrying a
// second hardcoded copy of the same color. Falls back to brand cyan if unrecognized.
function toRgb(color) {
    const hex = String(color)
        .trim()
        .match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
    if (hex) {
        let h = hex[1]
        if (h.length === 3)
            h = h
                .split('')
                .map((c) => c + c)
                .join('')
        return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16))
    }
    const parts = String(color).match(/[\d.]+/g)
    return parts ? parts.slice(0, 3).map(Number) : [0, 229, 255]
}

const rgba = ([r, g, b], alpha) => `rgba(${r}, ${g}, ${b}, ${alpha})`

// A safe 7×24 zero grid so the chart still renders when the range has no calls.
function emptyGrid() {
    return Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => ({ vol: 0, rcv: 0 })))
}

export function buildHourByDayDensity(
    canvas,
    optionsFromParam,
    { measure: initialMeasure = 'vol', plugins = [] } = {},
) {
    const grid = (optionsFromParam.data && optionsFromParam.data.grid) || emptyGrid()
    const palette = chartPalette()
    const cyan = toRgb(palette.cyan)
    const violet = toRgb(palette.violet)

    // The theme value was dropped from the controller (colors now come from CSS vars); read the
    // active Bootstrap color mode straight off <html> for the faint empty-cell / hairline tones.
    const dark = document.documentElement.getAttribute('data-bs-theme') === 'dark'

    let measure = initialMeasure // 'vol' | 'rcv' — seeded from the controller so it survives data reloads
    let currentMax = 1 // recomputed whenever the measure changes

    const val = (cell) => (measure === 'vol' ? cell.vol : cell.rcv)
    const maxOf = () => Math.max(1, ...grid.flat().map(val))

    // Faint zero-cell + hairline on dark surfaces; the same neutral reads fine on light cards.
    const emptyCell = dark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.03)'
    const cellBorder = dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)'
    const tickColor = palette.ink

    function cellColor(v) {
        if (v <= 0) return emptyCell
        const eased = Math.pow(v / currentMax, 0.65)
        const alpha = 0.1 + eased * 0.85
        return rgba(measure === 'vol' ? cyan : violet, alpha)
    }

    function buildMatrixData() {
        const points = []
        for (let d = 0; d < 7; d++) {
            for (let h = 0; h < 24; h++) {
                points.push({ x: h, y: DAYS[d], v: val(grid[d][h]), d, h })
            }
        }
        return points
    }

    function findPeak() {
        let best = { v: -1, d: 0, h: 0 }
        for (let d = 0; d < 7; d++) {
            for (let h = 0; h < 24; h++) {
                const v = val(grid[d][h])
                if (v > best.v) best = { v, d, h }
            }
        }
        return best
    }

    // Footer scale model for the shared htmlLegend plugin (charts_js_controller). It paints the
    // gradient rail + min/max labels into the card footer; we just describe it. The hue + max
    // follow the active measure (cyan ↔ violet); the peak rides the shared `rightLabel` slot
    // (the same right-aligned caption the chip legend's legendRightLabel uses). Re-read on every
    // chart.update() — the measure toggle's update repaints it for free.
    function colorScaleModel() {
        const vol = measure === 'vol'
        const hue = vol ? cyan : violet
        const maxV = maxOf()
        const peak = findPeak()
        return {
            stops: ['rgba(128,128,128,0.05)', rgba(hue, 0.35), rgba(hue, 1)],
            minText: '0',
            maxText: vol ? `${Math.round(maxV)} calls` : `${maxV.toFixed(0)}% receivables`,
            rightLabel:
                peak.v > 0
                    ? {
                          html: `<i class="fa-solid fa-arrow-trend-up me-1"></i>Peak: ${DAYS[peak.d]} ${hourLabel(peak.h)}`,
                          color: rgba(hue, 1),
                      }
                    : null,
        }
    }

    currentMax = maxOf()

    const chart = new Chart(canvas, {
        type: 'matrix',
        // Inline plugins from the controller — the shared htmlLegend renders the footer scale.
        plugins: [...plugins],
        data: {
            datasets: [
                {
                    label: 'Density',
                    data: buildMatrixData(),
                    backgroundColor: (ctx) => cellColor(ctx.raw.v),
                    borderColor: cellBorder,
                    borderWidth: 0.5,
                    borderRadius: 3,
                    width: ({ chart }) => ((chart.chartArea || {}).width || 0) / 24 - 3,
                    height: ({ chart }) => ((chart.chartArea || {}).height || 0) / 7 - 3,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 50,
            layout: { padding: { top: 2, right: 2 } },
            plugins: {
                legend: { display: false },
                // The footer color scale renders through the shared htmlLegend plugin (registered
                // via `plugins` above); the controller re-reads this builder on every update.
                htmlLegend: { colorScale: colorScaleModel },
                // Tooltip chrome (dark, cyan-bordered box) comes from the global theme; we hide
                // the swatch (single-value cells) and accent the border to the active measure.
                tooltip: {
                    displayColors: false,
                    borderColor: rgba(measure === 'vol' ? cyan : violet, 0.3),
                    callbacks: {
                        title: (items) => {
                            const r = items[0].raw
                            return `${DAYS[r.d]} · ${hourLabel(r.h)}`
                        },
                        label: (item) => {
                            const cell = grid[item.raw.d][item.raw.h]
                            if (measure === 'vol') return ` ${cell.vol} calls`
                            return cell.vol > 0
                                ? ` ${cell.rcv.toFixed(1)}% receivables`
                                : ' no calls'
                        },
                    },
                },
            },
            scales: {
                x: {
                    type: 'category',
                    labels: HOURS,
                    position: 'top',
                    offset: true,
                    grid: { display: false },
                    border: { display: false },
                    ticks: {
                        color: tickColor,
                        autoSkip: false,
                        maxRotation: 0,
                        // Label every other hour so 24 columns stay legible.
                        callback: (v, i) => (i % 2 === 0 ? hourLabel(i) : ''),
                    },
                },
                y: {
                    type: 'category',
                    labels: [...DAYS].reverse(),
                    offset: true,
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: tickColor, padding: 6 },
                },
            },
            animation: { duration: 300 },
        },
    })

    // Switch the measure by MUTATING the live chart — never destroy/recreate, so the matrix
    // cell-size functions always resolve against a live chartArea (a rebuilt chart paints
    // blank). The matrix plugin also needs an explicit draw() after update('none') to repaint
    // the recolored cells. update() also fires the htmlLegend plugin, which re-reads
    // colorScaleModel() and repaints the footer scale. Called from charts_js_controller.
    chart.setDensityMeasure = (next) => {
        if (next === measure) return
        measure = next
        currentMax = maxOf()
        chart.data.datasets[0].data = buildMatrixData()
        chart.options.plugins.tooltip.borderColor = rgba(measure === 'vol' ? cyan : violet, 0.3)
        chart.update('none')
        chart.draw()
    }

    return chart
}
